/**
 * Signing in, and holding the token every API call needs.
 *
 * Cloudflare Access never needed any of this: it intercepted the request before the
 * app loaded, so by the time React ran, the person was already authenticated and the
 * cookie rode along invisibly. Kinde authenticates the *app*, which means the app has
 * to own the flow — redirect out, come back with a code, exchange it, hold the token,
 * and refresh it before it expires.
 *
 * The token is deliberately not stored in `localStorage`. Kinde's default keeps it in
 * memory with a refresh token in a cookie, so a script that manages to run on this
 * origin cannot read a bearer for the budget out of storage — which matters more here
 * than the small cost of a silent re-auth on a cold load. `is_dangerously_use_local_storage`
 * is named the way it is for a reason; leave it off.
 *
 * `getToken()` is what the API client calls on every request. It is not a cached
 * value: the SDK returns the live one and refreshes underneath when it is close to
 * expiring, which is the whole reason `createClient` takes a function.
 */

import createKindeClient, { type KindeClient, type KindeUser } from '@kinde-oss/kinde-auth-pkce-js'
import { createContext, type ReactNode, useContext, useEffect, useState } from 'react'

/**
 * Build-time config, from `.env` — see `docs/AUTH.md`.
 *
 * Vite inlines these, so they are public. That is correct and not a leak: a SPA's
 * client id is not a secret, which is exactly why the flow uses PKCE instead of a
 * client secret it could not keep.
 */
const DOMAIN = import.meta.env.VITE_KINDE_DOMAIN ?? ''
const CLIENT_ID = import.meta.env.VITE_KINDE_CLIENT_ID ?? ''
const AUDIENCE = import.meta.env.VITE_KINDE_AUDIENCE ?? ''

/**
 * Which identity provider to go straight to, skipping Kinde's own page.
 *
 * Two people share this budget and both sign in with Google, so the hosted page is a
 * menu with one useful item on it — a screen to click through rather than a choice to
 * make. Naming the connection sends the browser to Google directly.
 *
 * Config rather than a constant: it is a Kinde id, it differs per environment, and left
 * blank the hosted page comes back — which is the right fallback if the connection is
 * ever removed, and the only way in if Google is down.
 */
const CONNECTION = import.meta.env.VITE_KINDE_CONNECTION_ID ?? ''

export interface AuthState {
  /** Null until the SDK has finished deciding, which is the "still checking" state. */
  signedIn: boolean | null
  user: KindeUser | null
  signIn: () => void
  signOut: () => void
  /** Set when Kinde itself refused — a wrong redirect URI, a revoked user. */
  error: string | null
}

const AuthContext = createContext<AuthState | null>(null)

/**
 * The client, created once outside React.
 *
 * A module-level promise rather than state, because `createKindeClient` is what
 * processes the `?code=` on the callback URL — running it twice would try to redeem a
 * single-use authorization code twice, and StrictMode double-invokes effects in
 * development precisely to catch this. The second exchange fails, and the failure
 * looks like a broken login rather than a double call.
 */
let clientPromise: Promise<KindeClient> | null = null

function client(): Promise<KindeClient> {
  if (!clientPromise) {
    clientPromise = createKindeClient({
      domain: DOMAIN,
      client_id: CLIENT_ID,
      redirect_uri: window.location.origin,
      logout_uri: window.location.origin,
      /*
       * `audience` is what makes Kinde mint a token *for the API* rather than a bare
       * ID token. Without it the access token has no `aud` the Worker can pin, and
       * every request fails verification — the failure mode is a login that appears
       * to succeed and an app that 401s on every call.
       */
      ...(AUDIENCE ? { audience: AUDIENCE } : {}),
      /*
       * `email` is not optional here. The Worker resolves the person by email against
       * the `members` table, so a token without the claim cannot be attributed to
       * anyone and is refused.
       */
      scope: 'openid profile email offline',
      on_redirect_callback: () => {
        // Strip `?code=…&state=…` so a reload does not attempt the exchange again and
        // the address bar is not carrying an authorization code around.
        window.history.replaceState({}, document.title, window.location.pathname)
      },
    })
  }
  return clientPromise
}

/** The live access token, or null when signed out. Read fresh on every request. */
export async function getToken(): Promise<string | null> {
  if (DOMAIN === '') return null
  try {
    return (await (await client()).getToken()) ?? null
  } catch {
    // A refresh that cannot be completed is a signed-out state, not a crash: the
    // request goes without a bearer, the API answers 401, and the app shows the
    // sign-in screen.
    return null
  }
}

/**
 * Drop the dead session and show the sign-in screen.
 *
 * Called from the query cache when the API says 401, which is the one place that
 * learns the refresh token is gone — the SDK cannot tell us, because from its side a
 * token it minted an hour ago is simply old, not rejected.
 *
 * `logout()` rather than a local flag: leaving Kinde's session standing while the app
 * claims to be signed out means the next sign-in silently reuses it and lands straight
 * back on the same dead state.
 */
export function signOutToLogin(): void {
  if (DOMAIN === '') return
  void client().then((kinde) => kinde.logout())
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [signedIn, setSignedIn] = useState<boolean | null>(null)
  const [user, setUser] = useState<KindeUser | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let live = true
    /*
     * Not configured yet is a deliberate pass-through, not a failure. It keeps the app
     * usable against a local dev API — which has no identity provider in front of it
     * and resolves the member from ALLOWED_EMAILS — so a fresh clone still runs with
     * no Kinde account, the same way blank Akahu tokens fall back to fixtures.
     */
    if (DOMAIN === '') {
      setSignedIn(true)
      return
    }
    client()
      .then(async (kinde) => {
        const ok = await kinde.isAuthenticated()
        if (!live) return
        setSignedIn(ok)
        setUser(ok ? kinde.getUser() : null)
      })
      .catch((cause: unknown) => {
        if (!live) return
        setError(cause instanceof Error ? cause.message : 'Could not reach the sign-in service.')
        setSignedIn(false)
      })
    return () => {
      live = false
    }
  }, [])

  const value: AuthState = {
    signedIn,
    user,
    error,
    signIn: () => {
      void client().then((kinde) => kinde.login(CONNECTION ? { connectionId: CONNECTION } : {}))
    },
    signOut: () => {
      void client().then((kinde) => kinde.logout())
    },
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthState {
  const value = useContext(AuthContext)
  if (!value) throw new Error('useAuth outside AuthProvider')
  return value
}
